import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useNativeLaunch } from "../lib/useNativeLaunch";
import WebPlayer from "../components/WebPlayer";
import { ArrowLeft, MonitorPlay, RotateCw } from "lucide-react";

export default function Player() {
  const { id } = useParams();
  const nav = useNavigate();
  const [movie, setMovie] = useState(null);
  const { status, launch, configReady, lastPlayer } = useNativeLaunch();
  // Web player is the default; Kodi is the fallback (unsupported media, busy server) or a saved preference.
  const [native, setNative] = useState(() => localStorage.getItem("kino_player_pref") === "kodi");
  const [reason, setReason] = useState("");
  const playerLabel = lastPlayer === "wmp" ? "Windows Media Player" : "Kodi";
  const otherPlayer = lastPlayer === "wmp" ? "kodi" : "wmp";
  const otherPlayerLabel = otherPlayer === "wmp" ? "Windows Media Player" : "Kodi";
  const useWebPlayer = () => { localStorage.setItem("kino_player_pref", "web"); setReason(""); setNative(false); };

  useEffect(() => {
    let cancelled = false;
    api.get(`/movies/${id}`).then(({ data }) => { if (!cancelled) setMovie(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Auto-launch once the movie AND the LAN-url config are both ready — launching before
  // configReady would silently fall back to window.location.origin (the public domain, if
  // that's what the browser session is on) instead of the LAN address.
  useEffect(() => {
    if (native && movie && configReady) launch(movie.id, "movie");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie, configReady, native]);

  if (!movie) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-[#8A8A8A]" data-testid="player-loading">Loading…</div>;
  }

  if (!native) {
    return (
      <WebPlayer key={movie.id} kind="movie" id={movie.id} title={movie.title}
        subtitle={[movie.year, movie.rating].filter(Boolean).join(" · ")}
        onKodi={(why) => { if (!why) localStorage.setItem("kino_player_pref", "kodi"); setReason(why || ""); setNative(true); }} />
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center gap-6 px-6" data-testid="player-page">
      <button onClick={() => nav(-1)}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/80 hover:text-white bg-black/60 hover:bg-black px-4 py-2 transition-colors duration-300"
        data-testid="player-back-button">
        <ArrowLeft size={16} strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.2em]">Back</span>
      </button>

      <h2 className="font-display text-2xl text-white tracking-tight text-center">{movie.title}</h2>
      {reason && <p className="text-[#8A8A8A] text-xs max-w-md text-center">{reason}</p>}

      {status === "launching" && (
        <div className="flex flex-col items-center gap-3" data-testid="player-launching">
          <div className="h-8 w-8 border-2 border-[#333] border-t-[#D9381E] rounded-full animate-spin" />
          <p className="text-[#8A8A8A] text-sm">Opening in {playerLabel}…</p>
        </div>
      )}

      {status === "launched" && (
        <div className="flex flex-col items-center gap-4" data-testid="player-launched">
          <MonitorPlay size={40} strokeWidth={1.5} className="text-[#86efac]" />
          <p className="text-white text-sm">Opened in {playerLabel}. You can close this tab.</p>
        </div>
      )}

      {status === "failed" && (
        <div className="flex flex-col items-center gap-4 max-w-md text-center" data-testid="player-install-prompt">
          <MonitorPlay size={40} strokeWidth={1.5} className="text-[#fca5a5]" />
          <p className="text-white">Couldn't find {playerLabel} or the StreamHoard Player helper on this computer.</p>
          {lastPlayer === "wmp" ? (
            <p className="text-[#8A8A8A] text-sm">
              Windows Media Player isn't installed by default on current Windows 10/11 — add it via
              Settings → Apps → Optional features → Add a feature → "Windows Media Player". Or try
              Kodi instead (full HEVC/DTS/Dolby Vision support).
            </p>
          ) : (
            <p className="text-[#8A8A8A] text-sm">Install Kodi, then try again — or try Windows Media Player instead, already installed on most Windows machines (simpler content only, no HEVC/DTS/Dolby Vision support).</p>
          )}
          {lastPlayer !== "wmp" && (
            <a href="https://kodi.tv/download" target="_blank" rel="noreferrer"
              className="bg-[#D9381E] hover:bg-[#ED4B32] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] text-center"
              data-testid="player-download-kodi">
              Download Kodi
            </a>
          )}
          <p className="text-[#666] text-xs">
            Kodi also needs the StreamHoard Player helper on this Windows PC (it registers the
            streamhoardplay:// link). Install it once by opening{" "}
            <span className="text-[#aaa] select-all">{"\u005c\u005c10.48.200.249\u005cvideos\u005cdownloads\u005cStreamHoard-Player"}</span>{" "}
            on your network and running <b>Install-StreamHoard-Player.cmd</b>, then try again.
          </p>
        </div>
      )}

      {/* Every player-choice / retry action lives together in this one group. */}
      <div className="flex flex-wrap items-center justify-center gap-2" data-testid="player-actions">
        {(status === "launched" || status === "failed") && (
          <button onClick={() => launch(movie.id, "movie", lastPlayer)}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
            data-testid={status === "launched" ? "player-relaunch" : "player-retry"}>
            <RotateCw size={13} strokeWidth={1.5} /> {status === "launched" ? "Open again" : "Try again"}
          </button>
        )}
        {(status === "launched" || status === "failed") && (
          <button onClick={() => launch(movie.id, "movie", otherPlayer)}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
            data-testid={status === "launched" ? "player-use-wmp" : "player-try-wmp"}>
            {status === "launched" ? "Play" : "Try"} in {otherPlayerLabel} instead
          </button>
        )}
        <button onClick={useWebPlayer}
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] hover:text-white border border-[#222] hover:border-white px-4 py-2 transition-colors"
          data-testid="player-use-web">
          Use web player
        </button>
      </div>
    </div>
  );
}
